import React, { FC, useState, useRef, useEffect } from 'react';
import { iconSize } from '../../../../../../../constants/icon.constants';
import Icon from '../../../../../../@shared/icon';
import { documentService } from '../../../../../../../utils/window.utils';
import { TCountry } from '../../../../../types/dealerLocator.types';
import ItemList from './itemList';

enum arrowIcons {
  up = 'arrow-chevron-up',
  down = 'arrow-chevron-down',
}

const Dropdown: FC<{
  items: Array<TCountry>;
  label?: string;
  onSelect: React.Dispatch<React.SetStateAction<Partial<TCountry | undefined>>>;
  country?: TCountry;
}> = ({ items, label, onSelect, country }) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>(country?.Country ?? '');
  const [searchTextChanged, setSearchTextChanged] = useState<boolean>(false);
  const [highligtedItemIndex, setHighligtedItemIndex] = useState<number | null>(null);
  const [dropdownHover, setDropdownHover] = useState<boolean>(false);
  const searchInputElmRef = useRef<HTMLInputElement>(null);
  const dropdownElmRef = useRef<HTMLDivElement>(null);
  const filteredItems = items?.filter((item) => {
    return item?.Country?.toLocaleLowerCase().includes(searchText?.toLocaleLowerCase());
  });

  const arrowIcon = dropdownOpen ? arrowIcons.up : arrowIcons.down;

  const visibleItems = searchTextChanged && searchText !== '' ? filteredItems : items;

  const closeDropdown = () => {
    setDropdownOpen(false);
    setSearchTextChanged(false);
    setHighligtedItemIndex(null);
  };

  const handleListItemSelection = (item: TCountry) => {
    setSearchText(item?.Country ?? '');
    onSelect(item);
    closeDropdown();
  };

  const handleInputClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e?.target?.value);
    setSearchTextChanged(true);

    if (!dropdownHover) {
      setHighligtedItemIndex(null);
    }
  };

  const keyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && visibleItems.length > 0) {
      if (searchText !== '' && highligtedItemIndex === null) {
        handleListItemSelection(visibleItems[0]);
      } else if (Number.isInteger(highligtedItemIndex)) {
        handleListItemSelection(visibleItems[highligtedItemIndex as number]);
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const index = highligtedItemIndex !== null && highligtedItemIndex < visibleItems.length - 1 ? highligtedItemIndex + 1 : 0;
      setHighligtedItemIndex(index);
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const index = highligtedItemIndex !== null && highligtedItemIndex > 0 ? highligtedItemIndex - 1 : visibleItems.length - 1;
      setHighligtedItemIndex(index);
    }
  };

  const handleClickOutside = (e) => {
    if (dropdownElmRef?.current?.contains(e?.target)) {
      return;
    }

    closeDropdown();
  };

  const getOptionText: (item: TCountry | null) => string = (item) => {
    if (!item) {
      return '';
    }

    return item?.Country ?? '';
  };

  const getOptionKey: (item: TCountry) => string = (item) => {
    return item?.Country ?? '';
  };

  useEffect(() => {
    if (dropdownOpen) {
      searchInputElmRef.current?.focus();
    }
  }, [dropdownOpen]);

  useEffect(() => {
    setSearchText('');
    setSearchTextChanged(false);
  }, [items]);

  useEffect(() => {
    const doc = documentService() as Document;

    if (dropdownOpen) {
      doc.addEventListener('click', handleClickOutside, false);
    }

    return (): void => {
      doc.removeEventListener('click', handleClickOutside, false);
    };
  }, [documentService, dropdownOpen]);

  return (
    <div className="preferred-dealer-search__dropdown" ref={dropdownElmRef}>
      {
        <div
          className={`preferred-dealer-search__dropdown-input ${dropdownOpen ? 'preferred-dealer-search__dropdown-input--open' : ''
            } `}
          onClick={() => handleInputClick()} // eslint-disable-line
        >
          <span
            className={`preferred-dealer-search__dropdown-label ${dropdownOpen || country ? '' : 'preferred-dealer-search__dropdown-label--center'
              }`}
          >
            {label}
          </span>
          {dropdownOpen ? (
            <input
              className="preferred-dealer-search__dropdown-input-field"
              type="text"
              value={searchText}
              ref={searchInputElmRef}
              onChange={handleInputChange}
              onKeyDown={keyDownHandler}
              autoComplete="off"
            />
          ) : (
            <span className="preferred-dealer-search__dropdown-input-text">{getOptionText(country ?? null)}</span>
          )}
          <Icon
            iconName={arrowIcon}
            extraClassName="preferred-dealer-search__dropdown-input-icon"
            size={iconSize['dsk']}
            color="gray"
          />
        </div>
      }
      {dropdownOpen && (
        <div className="preferred-dealer-search__text-search-suggestions">
          <ItemList
            items={visibleItems}
            highlightedSuggestionIndex={highligtedItemIndex}
            searchText={searchText}
            mouseEnterHandler={(index: number) => {
              setHighligtedItemIndex(index);
              setDropdownHover(true);
            }}
            clickHandler={handleListItemSelection}
            optionTextHandler={getOptionText}
            keyGenerator={getOptionKey}
          />
        </div>
      )}
    </div>
  );
};

export default Dropdown;
